/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019-2020 (original work) Open Assessment Technologies SA ;
 */

import { router, routingTable } from '@/core';
import { locale } from '@/services/localeService';
import '@oat-sa-private/ui-identity/css/main.css';
import packageJson from '../package.json';
import { extractDomain, log } from './core/utils';
import { user } from '@/stores/auth.store.js';
import { configurationStore, currentTenantStore } from './stores/tenantConfiguration.store';
import '@/stores/localeVersion.store.js';
import { storeTokens, getTenant } from '@/services/authService';
import ModalOutlet from '@/components/ModalOutlet/ModalOutlet.svelte';
import './locale/beMessages';
import './index.css';

window.__VERSION = packageJson.version;

function mountGlobalComponents() {
    const modalContainer = document.createElement('div');
    modalContainer.id = 'modal-outlet';
    document.body.appendChild(modalContainer);

    new ModalOutlet({ target: modalContainer });
}

async function startApplication() {
    mountGlobalComponents();
    locale.setDictionaryLoader();

    try {
        const domain = extractDomain(window.location.href);
        if (!domain) {
            const message = `Missing domain in window.location: ${window.location?.href ?? 'unknown'}`;
            log.warn(message);
            throw new Error(message);
        }

        try {
            await configurationStore.updateConfigurationData();
        } catch (error) {
            log.warn(`Failed to update configuration for domain ${domain}: ${error.message}`);
            throw error;
        }
        const configuration = configurationStore.get();
        const tenantId = configuration?.tenantId;

        if (!tenantId) {
            throw new Error(`Missing tenantId in configuration for domain: ${domain}`);
        }

        let tenantData = { tenantId, domain };
        try {
            const authTenantData = await getTenant();
            tenantData = { ...tenantData, ...authTenantData };
        } catch (error) {
            log.warn(`Failed to fetch tenant data from Auth Server: ${error.message}`);
        }
        currentTenantStore.set(tenantData);
    } catch (err) {
        log.warn(err.message);
        return;
    }

    if (window.SESSION_OVERRIDE) {
        await storeTokens(window.SESSION_OVERRIDE);
        log.warn('Session overridden', window.SESSION_OVERRIDE);
    }

    try {
        await user.updateInfo(); // throws error if token does not exist
        let { locale: defaultLocale } = configurationStore.get() || {};
        const { language: userLanguage } = user.get() || {};

        await locale.updateApplicationLocale(userLanguage || defaultLocale || '');
        if (user.get()?.userId) {
            await configurationStore.updateTenantRoles();
        }
    } catch (err) {
        log.warn(err.message);
    } finally {
        await router.start(routingTable, window.SESSION_OVERRIDE?.url);
    }
}

startApplication();
